import { LoadingButton } from "@mui/lab";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
	useGetMyTimeApprovalPreviewLazyQuery,
	useMyCreateTimeApprovalMutation,
} from "@repo/server-api";
import { ProjectTimeApprovalExtended } from "@repo/server-api/types";
import { useNotification } from "@repo/shared/components";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useState, useMemo, useEffect } from "react";
import * as Yup from "yup";

import {
	validErrorState,
	formatHoursMinutes,
	formatDate,
	formatTimeSpanToHHmm,
} from "@repo/legacy/components/helper/Index";

const DialogTimeApproval = (
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		handleClose?: (value: boolean) => void;
		classes?: string;
		refreshData: (t?: ProjectTimeApprovalExtended) => void;
		timeApproval: ProjectTimeApprovalExtended | null;
		setTimeApproval: (value: ProjectTimeApprovalExtended | null) => void;
	}>,
) => {
	const { t, i18n } = useTranslation();

	const { showNotification } = useNotification();

	const timeApproval = useMemo(() => props.timeApproval ?? null, [props.timeApproval]);

	const [openMonth, setOpenMonth] = useState<boolean>(false);

	const [getTimeApprovalPreview] = useGetMyTimeApprovalPreviewLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data.myTimeApprovalPreview) {
				props.setTimeApproval({
					...timeApproval,
					approvedEntries: data.myTimeApprovalPreview.entries.map((e) => ({
						...e,
						key: Math.random().toString(36).substring(2, 15),
					})),
				} as ProjectTimeApprovalExtended);
			}
		},
	});

	const open = useMemo(() => {
		if (props.open && timeApproval)
			getTimeApprovalPreview({
				variables: {
					projectId: timeApproval?.project?.id || "",
					month: timeApproval?.to || "",
				},
			});
		return props.open;
	}, [props.open]);

	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [createTimeApproval] = useMyCreateTimeApprovalMutation();

	const handleClose = () => {
		props.setOpen(false);
		if (props.handleClose) props.handleClose(false);
		setSaveClicked(false);
		setErrors({});
		props.setTimeApproval(null);
	};

	const validationSchema = Yup.object().shape({
		to: Yup.string()
			.matches(/^\d{4}-\d{2}-\d{2}$/, t("date-is-invalid", { ns: "validation" }))
			.required(t("date-is-required", { ns: "validation" })),
	});

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};
		try {
			await validationSchema.validate(timeApproval, { abortEarly: false });
			setErrors?.({});
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				err.inner.forEach((error) => {
					if (error.path) {
						localErrors[error.path] = error.message;
					}
				});
				setErrors?.({ ...localErrors });
			}
		}

		return validErrorState(localErrors);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();

		const isValid = await validateFields();

		if (isValid) {
			setSaveClicked(true);
			createTimeApproval({
				variables: {
					projectId: timeApproval?.project?.id || "",
					month: timeApproval?.to || "",
				},
			})
				.then((res) => {
					if (res.data?.myCreateTimeApproval.projectTimeApproval) {
						showNotification(t("projects.timeSheets.created"), "success");
						props.refreshData(
							res.data.myCreateTimeApproval.projectTimeApproval as ProjectTimeApprovalExtended,
						);
						handleClose();
					} else if (res.data?.myCreateTimeApproval.errors) {
						res.data.myCreateTimeApproval.errors.forEach((e: { message: string }) =>
							showNotification(e.message, "error"),
						);
					} else {
						handleClose();
					}
					setSaveClicked(false);
				})
				.catch((err) => {
					showNotification(err.message, "error");
					setSaveClicked(false);
				});
		}
	};

	useEffect(() => {
		if (timeApproval?.to) {
			getTimeApprovalPreview({
				variables: {
					projectId: timeApproval?.project?.id || "",
					month: timeApproval?.to || "",
				},
			});
		}
	}, [timeApproval?.to]);

	return (
		<div>
			<Dialog
				open={open}
				PaperProps={{
					component: "form",
					onSubmit: handleSubmit,
				}}
				maxWidth="md"
				fullWidth={true}>
				<DialogTitle>{t("create-new-timesheet")}</DialogTitle>
				<DialogContent>
					<Box sx={{ marginTop: "10px" }}>
						<TextField
							sx={{ width: "40%" }}
							label={t("project-name")}
							value={timeApproval?.project?.name || ""}
							variant="outlined"
							InputProps={{
								readOnly: true,
							}}
						/>
						<TextField
							sx={{ width: "40%", float: "right" }}
							label={t("employee")}
							value={timeApproval?.employee.displayName || ""}
							variant="outlined"
							InputProps={{
								readOnly: true,
							}}
						/>
					</Box>
					<Box sx={{ marginTop: "20px", marginBottom: "10px" }}>
						<Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
							<Box sx={{ flex: 1 }}>
								<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language || "de"}>
									<DatePicker
										views={["year", "month"]}
										open={openMonth}
										onClose={() => setOpenMonth(false)}
										onOpen={() => setOpenMonth(true)}
										format={"MMMM YYYY"}
										sx={{ width: "100%", fontColor: "black" }}
										label={t("month")}
										value={timeApproval?.to ? dayjs(timeApproval.to) : null}
										onChange={(newValue) => {
											props.setTimeApproval({
												...timeApproval,
												to: newValue ? dayjs(newValue).format("YYYY-MM-DD") : "",
											} as ProjectTimeApprovalExtended);
											setErrors({ ...errors, to: "" });
										}}
										slotProps={{
											textField: {
												helperText: errors?.to as string,
												error: !!errors?.to,
												onClick: () => setOpenMonth(!openMonth),
											},
										}}
									/>
								</LocalizationProvider>
							</Box>
							<Button
								variant="outlined"
								onClick={() => {
									const prevMonth = dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
									props.setTimeApproval({
										...timeApproval,
										to: prevMonth,
									} as ProjectTimeApprovalExtended);
									setErrors({ ...errors, to: "" });
								}}>
								{t("previous-month")}
							</Button>
							<Button
								variant="outlined"
								onClick={() => {
									const currentMonth = dayjs().startOf("month").format("YYYY-MM-DD");
									props.setTimeApproval({
										...timeApproval,
										to: currentMonth,
									} as ProjectTimeApprovalExtended);
									setErrors({ ...errors, to: "" });
								}}>
								{t("current-month")}
							</Button>
						</Box>
					</Box>
					<DataGridPremium
						getRowHeight={() => "auto"}
						columns={[
							{
								field: "range",
								headerName: t("date-range"),
								flex: 1,
								align: "center",
								headerAlign: "center",
								renderCell: (params) => {
									const date = params.row.date as string;
									const startTime = formatTimeSpanToHHmm(params.row.startTime as string);
									const endTime = formatTimeSpanToHHmm(params.row.endTime as string);
									return (
										<Box sx={{ display: "flex", flexDirection: "column" }}>
											<div>{formatDate(date, i18n.language)}</div>
											<div>
												{startTime} - {endTime}
											</div>
										</Box>
									);
								},
							},
							{
								field: "description",
								headerName: t("no-description"),
								flex: 1,
								renderCell: (params) => {
									return (
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
												height: "100%",
											}}>
											{params.row.description || ""}
										</Box>
									);
								},
							},
							{
								field: "hours",
								headerName: t("hours"),
								flex: 1,
								renderCell: (params) => {
									return (
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
												height: "100%",
											}}>
											{formatHoursMinutes(params.row.totalMinutes as number)}
										</Box>
									);
								},
							},
						]}
						rows={timeApproval?.approvedEntries || []}
						getRowId={(row) => row.key as string}
						disableRowSelectionOnClick
						disableColumnMenu
						disableColumnFilter
						disableColumnSelector
						disableDensitySelector
						hideFooter
					/>
					<Box sx={{ marginTop: "20px" }}>
						{t("total-hours")}:{" "}
						{formatHoursMinutes(
							(timeApproval?.approvedEntries ?? []).reduce(
								(acc, entry) => acc + (entry.totalMinutes ?? 0),
								0,
							),
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button data-testid="close-button" variant="outlined" onClick={handleClose}>
						{t("cancel")}
					</Button>
					<LoadingButton
						disabled={timeApproval?.approvedEntries?.length === 0}
						loading={saveClicked}
						type="submit"
						variant="contained"
						className={saveClicked ? "" : "blueButton"}>
						{t("create")}
					</LoadingButton>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default DialogTimeApproval;
